<template>
  <v-dialog
    width="550"
    v-model="opened"
    persistent
    @keydown.esc="$emit('update:opened', false)"
  >
    <input
      type="file"
      accept=".pdf"
      style="display: none"
      multiple
      ref="selectedDocuments"
      @change="onDocumentsPicked"
    />

    <v-card flat @drop.prevent="addFile" @dragover.prevent>
      <v-card-title class="py-4 title text-xs-left">
        <h4>Upload Tax Return</h4>
      </v-card-title>

      <v-card-text class="pt-0">
        <v-alert
          :value="true"
          outline
          block
          :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
          color="primary"
          class="text-xs-left mb-2"
        >
          You can drag and drop files here. Only PDF files are supported.
        </v-alert>

        <v-flex class="text-xs-left">
          <v-layout align-center>
            <div class="py-3 font-weight-bold">
              {{ $t('documents.selectedDocuments') }}:
            </div>
            <v-spacer />
            <v-btn
              outline
              class="default-input text-capitalize primary--text"
              :disabled="documentsImporting"
              @click="pickDocuments"
            >
              <v-layout align-center>
                <v-icon class="mr-2" size="20">add</v-icon>
                <div v-if="!documentData">Select File</div>
                <div v-else>Select Other File</div>
              </v-layout>
            </v-btn>
          </v-layout>
        </v-flex>

        <v-layout align-center class="text-xs-left">
          <v-flex class="text-xs-left mb-4" v-if="isEmpty(documentsToImport)">
            No document selected.
          </v-flex>
          <v-flex class="mb-4" v-else>
            <v-layout
              align-center
              :key="i"
              v-for="(doc, i) in documentsToImport"
            >
              <v-icon color="primary"> insert_drive_file </v-icon>
              <span class="ml-1 my-1">{{ doc.name }}</span>
              <v-spacer />
              <v-icon
                @click="documentsToImport.splice(i, 1)"
                style="cursor: pointer"
                color="error"
                >cancel</v-icon
              >
            </v-layout>
          </v-flex>
        </v-layout>

        <v-layout wrap class="text-xs-left" v-if="!hideEntitySelect">
          <v-flex class="mb-4">
            <div class="font-weight-bold mb-2">
              <span>Send to:</span>
              <span class="error--text">*</span>
            </div>

            <v-autocomplete
              class="default-input body-1"
              hide-details
              :loading="loadingCompanies"
              :disabled="loadingCompanies"
              solo
              :items="entitiesWithClient"
              v-model="selectedEntityId"
              placeholder="Select entity"
              item-text="name"
              item-value="id"
            >
            </v-autocomplete>
          </v-flex>
        </v-layout>

        <v-layout wrap class="text-xs-left">
          <v-flex class="mb-4">
            <div class="font-weight-bold mb-2">
              Tax year:<span class="error--text ml-1">*</span
              ><v-tooltip top
                ><v-icon slot="activator" size="15" class="ml-1">info</v-icon
                ><span
                  >Specify which folder this document should be organized
                  into</span
                ></v-tooltip
              >
            </div>

            <v-autocomplete
              class="default-input body-1"
              solo
              :rules="rules.presence"
              :items="yearOptions"
              v-model="taxYear"
              placeholder="Select year"
            />
          </v-flex>
        </v-layout>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          class="text-capitalize primary"
          @click="upload"
          :loading="documentsImporting"
          :disabled="!canUpload"
        >
          Upload
        </v-btn>
        <v-btn
          class="text-capitalize primary--text"
          outline
          @click="
            $emit('update:opened', false);
            documentsToImport = [];
          "
        >
          {{ $t('cancel') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import has from 'lodash/has';
import get from 'lodash/get';
import API from '@/api';
import { mapState, mapGetters } from 'vuex';
import { rules, yearOptions } from '@/constants/variables';
import UtilsMixins from '@/mixins/UtilsMixins';

export default {
  mixins: [UtilsMixins],
  props: {
    opened: {
      type: Boolean,
    },

    currentCompany: {
      type: Object,
    },

    hideEntitySelect: {
      type: Boolean,
    },

    sectionId: {
      type: Number,
      optional: true,
    },
  },

  data() {
    return {
      rules,
      yearOptions,

      taxYear: null,

      documentData: '',
      documentsToImport: [],
      documentsImporting: false,

      selectedEntityId: this.currentCompany ? this.currentCompany.id : null,
    };
  },

  methods: {
    isEmpty,

    pickDocuments() {
      const input = this.$refs.selectedDocuments;
      input.type = 'text';
      input.type = 'file';
      this.$refs.selectedDocuments.click();
    },

    async onDocumentsPicked(e) {
      this.documentsImporting = true;
      const documents = e.target.files;

      if (documents) {
        this.documentsToImport.push(...documents);
        this.documentsImporting = false;
      }
    },

    async addFile(e) {
      this.documentImporting = true;
      let droppedFiles = e.dataTransfer.files;
      if (!droppedFiles) return;

      this.documentsToImport.push(...droppedFiles);
      this.documentImporting = false;
    },

    async upload() {
      this.documentsImporting = true;

      try {
        let fail = 0;
        for (const documentToImport of this.documentsToImport) {
          const trueExtension = await this.getTrueExtension(documentToImport);
          const currentExtension = this.getExtensionOfFilename(
            documentToImport.name,
          );
          const extensionsToCheck = ['pdf'];

          if (documentToImport.size === 0) {
            throw new Error(
              `We were unable to process some of uploaded files as they appear to be EMPTY. Kindly check those files and try uploading them again.`,
            );
          }

          if (
            extensionsToCheck.includes(currentExtension) &&
            trueExtension !== currentExtension
          ) {
            throw new Error(
              `We were unable to process some of uploaded files as they appear to be INVALID. Kindly check those files and try uploading them again.`,
            );
          }

          if (currentExtension === 'pdf') {
            if (await this.pdfHasPassword(documentToImport)) {
              throw new Error(
                `The PDFs you uploaded are password-protected. Please either remove the password or upload alternative files.`,
              );
            }
          }

          const {
            data: { id, upload_link },
          } = await API.integration.getTemporaryUploadLink({
            companyId: this.selectedEntityId,
          });

          await API.integration.uploadToDropbox(upload_link, documentToImport);

          const response = await API.integration.processDocument({
            id,
            documentName: documentToImport.name,
            companyId: this.selectedEntityId,
            path: `/_soraban/Tax Return Delivery/${this.taxYear}`,
            documentType: 'tax_return_delivery',
            taxYear: this.taxYear,
            sectionId: this.sectionId,
          });

          if (response.data.error) {
            fail += 1;
            console.log(response.data.error);
          } else {
            this.$emit('refresh-table', response.data.document_location);
          }
        }

        if (fail === 0) {
          this.$store.dispatch('application/setSuccessNotification', {
            message:
              'Successfully uploaded. Please wait a few minutes for the document to be processed.',
          });

          this.$emit('update:opened', false);
          this.$emit('uploaded');
        } else {
          this.$store.dispatch('application/setErrorNotification', {
            message: `There was a problem uploading ${fail} files.`,
          });
        }
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: has(e, 'message')
            ? get(e, 'message')
            : `There was a problem uploading the document${
                this.documentsToImport.length > 1 ? 's' : ''
              }.`,
          errorToSend: e,
        });
      } finally {
        this.userImportDialogOpened = false;
        this.documentsImporting = false;
        this.documentsToImport = [];
      }
    },

    getDisplayName(entity) {
      if (this.isIndividual(entity)) {
        return entity.tax_return_name;
      } else {
        return `${entity.name} (${entity.user.full_name})`;
      }
    },

    isIndividual(entity) {
      return entity.entity_type === 'Individual';
    },
  },

  computed: {
    ...mapState('companies', ['companies', 'loadingCompanies']),

    ...mapGetters({
      currentUser: 'user',
    }),

    entitiesWithClient() {
      return this.companies
        .filter(
          c =>
            !this.currentUser.overview_setting.show_assigned_clients ||
            c.user.assigned_advisor_id === this.currentUser.id,
        )
        .map(entity => ({
          id: entity.id,
          name: `${this.getDisplayName(entity)}${
            entity.user.for_testing ? ' (Test)' : ''
          }`,
          entity_type: entity.entity_type,
          for_testing: entity.user.for_testing,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },

    canUpload() {
      return (
        this.selectedEntityId &&
        !isEmpty(this.documentsToImport) &&
        this.taxYear
      );
    },
  },
};
</script>
